/**
 * Data Access Layer for API Keys
 * 
 * API keys are used for authenticating API requests.
 * Keys are hashed and never stored in plain text.
 */

import { getClient, getAdminClient, hasAdminClient } from './client.js';
import { TABLES } from './tables.js';
import type {
  ApiKey,
  ApiKeyCreate,
  ApiKeyWithPlainText,
  ApiKeyValidationResult,
} from './types.js';

// ============================================
// API Key Operations
// ============================================

export async function listApiKeys(accountId: string): Promise<ApiKey[]> {
  if (!accountId) {
    throw new Error('Account ID is required');
  }

  try {
    console.log('🔍 listApiKeys called with accountId:', accountId);
    const client = getClient();
    console.log('🔍 PostgREST client:', client ? 'available' : 'missing');
    
    const query = client
      .from(TABLES.api_keys)
      .select('*')
      .eq('account_id', accountId)
      .order('created_at', { ascending: false });
    
    console.log('🔍 Query built, executing...');
    const { data, error } = await query;
    
    console.log('🔍 Query result:', { 
      hasData: !!data, 
      dataLength: data?.length || 0, 
      hasError: !!error,
      error: error ? JSON.stringify(error, null, 2) : null
    });

    if (error) {
      console.error('❌ Error listing API keys:', error);
      throw error;
    }

    return (data || []) as ApiKey[];
  } catch (err) {
    console.error('❌ Exception in listApiKeys:', err);
    throw err;
  }
}

export async function getApiKey(id: string): Promise<ApiKey | null> {
  const { data, error } = await getClient()
    .from(TABLES.api_keys)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as ApiKey;
}

/**
 * Generate a new API key
 * Returns the plain text key (only shown once) and stores the hash
 */
export async function generateApiKey(
  accountId: string,
  userId: string | null,
  input: ApiKeyCreate
): Promise<ApiKeyWithPlainText> {
  const { data, error } = await getClient()
    .rpc('generate_api_key', {
      p_account_id: accountId,
      p_created_by_user_id: userId,
      p_name: input.name,
      p_expires_at: input.expires_at || null,
    });

  if (error) throw error;

  if (!data || data.length === 0) {
    throw new Error('Failed to generate API key');
  }

  const result = data[0];
  return {
    id: result.id,
    account_id: accountId,
    created_by_user_id: userId,
    name: input.name,
    key_prefix: result.key_prefix,
    key: result.key, // Plain text key (only returned once)
    last_used_at: null,
    expires_at: input.expires_at || null,
    is_active: true,
    metadata: input.metadata || {},
    created_at: result.created_at,
    updated_at: result.created_at,
  };
}

/**
 * Validate an API key and return account information
 * Updates last_used_at automatically
 */
export async function validateApiKey(key: string): Promise<ApiKeyValidationResult | null> {
  if (!key) {
    console.log('🔍 validateApiKey: No key provided');
    return null;
  }

  // Trim whitespace and validate format
  const trimmedKey = key.trim();
  if (!trimmedKey.startsWith('lb_')) {
    console.log('⚠️ validateApiKey: Key does not start with "lb_"');
    return null;
  }

  try {
    console.log('🔍 validateApiKey: Validating key', {
      prefix: trimmedKey.substring(0, 15),
      length: trimmedKey.length,
      expectedLength: '76 (lb_live_ + 64 hex chars)',
    });
    const client = getClient();
    console.log('🔍 validateApiKey: Client available:', !!client);
    
    const { data, error } = await client
      .rpc('validate_api_key', { input_key: trimmedKey });

    console.log('🔍 validateApiKey: RPC call completed', {
      hasData: !!data,
      dataLength: data?.length || 0,
      hasError: !!error,
      error: error ? JSON.stringify(error, Object.getOwnPropertyNames(error)) : null,
    });

    if (error) {
      console.error('❌ API key validation error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
      return null;
    }

    if (!data || data.length === 0) {
      console.log('⚠️ API key validation: Key not found or invalid (RPC returned empty result)');
      return null;
    }

    console.log('✅ API key validation: Key is valid for account:', data[0].account_id);
    return data[0] as ApiKeyValidationResult;
  } catch (error) {
    console.error('❌ API key validation exception:', {
      error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return null;
  }
}

/**
 * Revoke (deactivate) an API key
 */
export async function revokeApiKey(keyId: string, accountId: string): Promise<void> {
  const { data, error } = await getClient()
    .rpc('revoke_api_key', {
      p_key_id: keyId,
      p_account_id: accountId,
    });

  if (error) throw error;

  if (!data) {
    throw new Error('Failed to revoke API key');
  }
}

/**
 * Update API key metadata
 */
export async function updateApiKey(
  keyId: string,
  accountId: string,
  updates: {
    name?: string;
    is_active?: boolean;
    expires_at?: string | null;
    metadata?: Record<string, unknown>;
  }
): Promise<ApiKey> {
  const { data, error } = await getClient()
    .from(TABLES.api_keys)
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', keyId)
    .eq('account_id', accountId)
    .select()
    .single();

  if (error) throw error;
  return data as ApiKey;
}

// ============================================
// Admin Operations (bypass RLS)
// ============================================

function requireAdminClient() {
  if (!hasAdminClient()) {
    throw new Error(
      'Admin client not available. Provide SUPABASE_SERVICE_ROLE_KEY to enable admin operations.'
    );
  }
  return getAdminClient()!;
}

export async function adminListApiKeys(accountId?: string): Promise<ApiKey[]> {
  const client = requireAdminClient();
  let query = client.from(TABLES.api_keys).select('*');

  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ApiKey[];
}

export async function adminGetApiKey(id: string): Promise<ApiKey | null> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.api_keys)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as ApiKey;
}
